import CalendarRentalPeriod from '@/components/calendar/CalendarRentalPeriod';
import { Button } from '@/components/common/Button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/common/Carousel';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import { CalenderPlusIcon, ClockIcon, FilterIcon, FlagLocation, InfoIcon, LocationIcon, PinLocation, StartRecomentIcon } from '@/components/icon';
import Paginate from '@/components/pagination/Paginate';
import { DATE_YMD, DISPPLAY_MOMENT_YMD, ekDomain, GEARBOX_SELECT, ROUTES, SEAT_HEIGHT_SELECT } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { COMMON_TYPE, LANGUAGES_SUPPORTED } from '@/types/enums';
import { formatDateTime } from '@/utils/datetime';
import { isPathEk } from '@/utils/routes';
import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronRight, X } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import LocationItem from '../LocationItem';
import InfoItem from './components/InfoItem';
import RentalPeriodModal from './components/RentalPeriodModal';
import { format } from 'date-fns';
import MapLocationCustom from '@/pages/booking/bike-detail/components/MapLocationCustom';
import BikeItem from './components/BikeItem';
import { SubTitleWithBox, SubTitleWithTwoBox } from '@/components/general/title/sub-title/SubTitle';
import TitleList from '@/components/general/title/TitleList';
import BannerLayout from '@/components/layout/banner-layout';
import ComboboxFilter from '@/components/common/combobox/ComboboxFilter';
import ErrorBoundary from '@/components/error-boundary/ErrorBoundary';

const DateRangeSchema = yup.object().shape({
    from: yup.date().nullable(),
    to: yup.date().nullable(),
    rentalTime: yup.string().nullable(),
    returnTime: yup.string().nullable()
});
// validate schema
const validateSchema = yup.object().shape({
    isRecommendation: yup.boolean(),
    engineSizeId: yup.string(),
    bikeTransmission: yup.string(),
    bikeType: yup.string(),
    seatHeight: yup.string(),
    dateTime: DateRangeSchema.optional().nullable().transform(value => (value === '' ? undefined : value))
});

type FormData = yup.InferType<typeof validateSchema>;

interface LocationDetailPageProps {
    showBanner?: boolean
}
export default observer(function LocationDetailPage({ showBanner = true }: LocationDetailPageProps) {
    // hook
    const { t, i18n: { language } } = useTranslation();
    const { pathname, hash } = useLocation();
    const { id } = useParams();

    const isEk = isPathEk(pathname);

    const navigate = useNavigate();

    // store
    const {
        capacitieStore: { getCapacitieList, capacitieList },
        locationBikeStore: {
            locations,
            listBikeLocation,
            bikePaging,
            defaultPagingCustom,
            totalBikePage,
            keepSearchParams,
            getAllLocationBike,
            getAllBikeInLocation,
            setObservable,
            clean: cleanLocationBike
        },
        locationEnduserStore: {
            locationDetail,
            getLocationDetail,
            setObservable: setObservableLocationEU,
            clean
        },
        generalStore: { getBikeTypes, bikeTypes },
        modalStore: { showModal }
    } = useStore();

    // state
    const listRef = useRef(null);
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            isRecommendation: undefined,
            engineSizeId: '',
            bikeTransmission: '',
            bikeType: '',
            seatHeight: '',
            dateTime: null
        }

    });

    const [isClearRental, setIsClearRental] = useState<boolean>(false);
    const formWatch = form.watch();
    const isRecommendationWatch = form.watch('isRecommendation');
    const [dataLoaded, setDataLoaded] = useState(false);

    // lifecycle
    useEffect(() => {
        getBikeTypes();
        getCapacitieList({
            type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS,
            sortKey: 'id',
            sortDir: 'asc'
        });
    }, []);

    useEffect(() => {
        if (id) {
            fetchData(id);
        }

        return () => {
            cleanLocationBike();
            clean();
        };
    }, [id]);

    useEffect(() => {
        return () => {
            form.reset({
                isRecommendation: undefined,
                engineSizeId: '',
                bikeTransmission: '',
                bikeType: '',
                seatHeight: '',
                dateTime: null
            });
            setObservable('keepSearchParams', undefined);
        };
    }, [locationDetail]);

    useEffect(() => {
        if (typeof formWatch !== 'object') return;
        const safeFormWatch = {
            ...formWatch,
            id,
            dateTime: formWatch.dateTime && typeof formWatch.dateTime === 'object' ? formWatch.dateTime : {}
        };
        const validValues = Object.fromEntries(
            Object.entries(safeFormWatch).filter(([_, value]) => value !== undefined && value !== null && value !== '')
        );
        if (Object.keys(validValues).length > 0) {
            onSearch(validValues);
        }
    }, [JSON.stringify(formWatch)]);

    useEffect(() => {
        if (!dataLoaded || !hash) return;
        const id = hash.substring(1);
        const el = document.getElementById(id);
        if (el) {
            const offset = 190; // khoảng cách muốn chừa trên cùng
            const top = el.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    }, [dataLoaded, hash]);

    // functions
    const fetchData = async (id: string) => {
        clean();
        const res = await flowResult(getLocationDetail({ id, type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS }));
        if (res) {
            setDataLoaded(true);
            await flowResult(getAllBikeInLocation(
                {
                    id: res.data.id,
                    type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS
                },
                defaultPagingCustom
            ));
            await flowResult(getAllLocationBike({
                provinceId: res.data.province.id,
                type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS
            }));
        }
    };

    const onSearch = (data: any) => {
        const { dateTime, ...newData } = data;
        const payload = {
            ...newData,
            rentalFrom: data?.dateTime?.from ?
                `${formatDateTime(data.dateTime.from, DISPPLAY_MOMENT_YMD)} ${data?.dateTime?.rentalTime === '' ? '00:00' : data.dateTime.rentalTime}` :
                undefined,
            rentalTo: data?.dateTime?.to ?
                `${formatDateTime(data.dateTime.to, DISPPLAY_MOMENT_YMD)} ${data?.dateTime?.returnTime === '' ? '23:59' : data.dateTime.returnTime}` :
                undefined

        };
        setObservable('keepSearchParams', Object.keys(data?.dateTime).length === 0 || keepSearchParams?.dateTime === '' ? newData : data);
        getAllBikeInLocation({ id: locationDetail?.id, type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS, ...payload }, defaultPagingCustom);
    };

    const handleClickViewAll = () => {
        const tmpLocationDetail = {
            ...locationDetail
        };
        navigate(isEk ? `/${ekDomain}/${ROUTES.locationList.href}` : `/${ROUTES.locationList.href}`);

        setObservable('keepSearchParams', { provinceId: tmpLocationDetail?.province?.id, regionId: tmpLocationDetail?.region?.id });

        setObservableLocationEU('keepSearchParams', { provinceId: tmpLocationDetail?.province?.id, regionId: tmpLocationDetail?.region?.id });
        setObservableLocationEU('locationDetail', undefined);
    };

    return (
        <div className='overflow-x-hidden'>
            {showBanner && (
                <BannerLayout
                    title='拠点からバイクを探す'
                    subTitle='拠点からバイクを探す'
                    url=''
                    className='hidden sm:block'
                />
            )}
            <div className='wrapper-inner pt-[min(120px,15vw)] under-wrapper'>
                <div className='grid point-900:grid-cols-2 gap-[min(40px,7.5vw)]'>
                    {/* Infor location */}
                    <div className='flex-col w-full overflow-hidden'>
                        <div className='mb-[min(40px,5vw)]'>
                            <SubTitleWithTwoBox
                                title={locationDetail?.name}
                                sub='Base name'
                                titleClassName='break-all'
                            />
                        </div>

                        <div className='space-y-2.5'>
                            <InfoItem
                                icon={<FlagLocation className='size-4' />}
                                title={t('words_title.location_type')}
                                text={locationDetail?.price?.name || '--'}
                            />
                            <InfoItem
                                icon={<LocationIcon className='size-4' />}
                                title={t('field.address')}
                                text={locationDetail?.address || '--'}
                            />
                            <InfoItem
                                icon={<PinLocation className='size-4' />}
                                title={t('words_title.access')}
                                text={locationDetail?.access || '--'}
                            />
                            <InfoItem
                                icon={<ClockIcon className='size-4' />}
                                title={t('words_title.operation_hour')}
                                text={locationDetail?.isShow24h ? t('words_title.open_24h') : `${locationDetail?.openTimeFrom?.split(':').slice(0, 2).join(':')} - ${locationDetail?.openTimeTo?.split(':').slice(0, 2).join(':')}`}
                            />
                            <InfoItem
                                icon={<InfoIcon className='size-4' />}
                                title='その他'
                                text={locationDetail?.note || '--'}
                            />
                        </div>
                    </div>

                    {/* Map */}
                    <div className='h-[286px] point-900:h-full'>
                        <ErrorBoundary className='w-full h-full'>
                            <MapLocationCustom locationStore={locationDetail} />
                        </ErrorBoundary>
                    </div>
                </div>

                <div ref={listRef} className='pt-[min(80px,10.25vw)] flex flex-col gap-[min(40px,7.5vw)]'>
                    {/* Title */}
                    <TitleList title={t('words_title.list_bike_of_location')} />

                    {/* Filter */}

                    <div id='list-bike' className='flex flex-col gap-y-2.5'>
                        <div className='flex md:hidden items-center gap-[5px] '>
                            <FilterIcon />
                            <span className='whitespace-nowrap'>絞り込み検索</span>
                        </div>
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSearch)} className='px-0 md:px-[10px] flex flex-wrap items-start md:items-center gap-x-[min(20px,2.6vw)] gap-y-2.5'>
                                <div className='hidden md:flex items-center gap-[5px] '>
                                    <FilterIcon />
                                    <span className='whitespace-nowrap'>絞り込み検索</span>
                                </div>

                                <div className='h-5 w-[1px] bg-border-gray hidden md:inline-block'></div>

                                <FormField
                                    control={form.control}
                                    name='isRecommendation'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <Button
                                                    type='button'
                                                    onClick={() => {
                                                        form.setValue('isRecommendation', isRecommendationWatch ? undefined : true);
                                                    }}
                                                    variant={isRecommendationWatch ? 'filterActive' : 'filterDefault'}
                                                    size='filter'
                                                    className='flex items-center justify-center gap-[5px]'
                                                >
                                                    <StartRecomentIcon />
                                                    {t('button.decommendation')}
                                                </Button>
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                {!isEk && (
                                    <>
                                        <FormField
                                            control={form.control}
                                            name='engineSizeId'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <ComboboxFilter
                                                            search={false}
                                                            placeholder={t('field.engine_size')}
                                                            value={field.value ?? ''}
                                                            onChange={(item) => {
                                                                field.onChange(item?.code ?? '');
                                                            }}
                                                            items={capacitieList.map(item => ({
                                                                label: language === LANGUAGES_SUPPORTED.Japanese ? item.nameJP : item.name,
                                                                value: String(item?.id),
                                                                code: item.id
                                                            }))}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='bikeTransmission'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <ComboboxFilter
                                                            search={false}
                                                            placeholder={t('field.gearbox')}
                                                            value={field.value ?? ''}
                                                            onChange={(item) => {
                                                                field.onChange(item?.code ?? '');
                                                            }}
                                                            items={GEARBOX_SELECT.map(item => ({
                                                                label: `${t(item.name)}`,
                                                                value: String(item?.value),
                                                                code: item.value
                                                            }))}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name='bikeType'
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormControl>
                                                        <ComboboxFilter
                                                            search={false}
                                                            placeholder={t('button.by_type')}
                                                            value={field.value ?? ''}
                                                            onChange={(item) => {
                                                                field.onChange(item?.code ?? '');
                                                            }}
                                                            items={bikeTypes.map(item => ({
                                                                label: language === LANGUAGES_SUPPORTED.Japanese ? item.name_jp : item.name,
                                                                value: String(item?.id),
                                                                code: item.id
                                                            }))}
                                                        />
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                    </>
                                )}
                                <FormField
                                    control={form.control}
                                    name='dateTime'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <>
                                                    <CalendarRentalPeriod
                                                        mode='range'
                                                        selected={field.value}
                                                        onSelect={date => field.onChange(date ?? undefined)}
                                                        required={false}
                                                        placeholder={t('button.rental_period')}
                                                        isClearRental={isClearRental}
                                                        setIsClearRental={setIsClearRental}
                                                        className='hidden md:flex'
                                                    />
                                                    <Button
                                                        variant={field.value ? 'filterActive' : 'filterDefault'}
                                                        size='filter'
                                                        type='button'
                                                        className='flex md:hidden items-center justify-start gap-[5px]'
                                                        onClick={() => {
                                                            showModal({
                                                                id: 'rental-period-modal',
                                                                size: 'xs',
                                                                title: t('words_title.rental_period'),
                                                                titleClassName: 'text-center w-full',
                                                                headerClassName: 'hidden',
                                                                footerClassName: 'hidden',
                                                                content: (
                                                                    <RentalPeriodModal
                                                                        selected={field.value}
                                                                        onSelect={date => field.onChange(date ?? undefined)}
                                                                        mode='range'
                                                                        required={false}
                                                                    />
                                                                )
                                                            });
                                                        }}
                                                    >
                                                        <CalenderPlusIcon />
                                                        {field?.value?.from ?
                                                            (
                                                                <span className='pr-1'>
                                                                    {`${format(field.value.from, DATE_YMD)} ${field.value.rentalTime} - ${field.value.to ? format(field.value.to, DATE_YMD) : ''} ${field.value.returnTime}`}
                                                                </span>
                                                            ) :
                                                            (
                                                                <span className=''>{t('button.rental_period')}</span>
                                                            )}
                                                        {field.value && (
                                                            <X
                                                                strokeWidth={1}
                                                                className='h-4 w-4'
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    field.onChange(undefined);
                                                                }}
                                                            />
                                                        )}
                                                    </Button>
                                                </>
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                {!isEk && (
                                    <FormField
                                        control={form.control}
                                        name='seatHeight'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <ComboboxFilter
                                                        search={false}
                                                        placeholder={t('field.seat_height')}
                                                        value={field.value ?? ''}
                                                        onChange={(item) => {
                                                            field.onChange(item?.code ?? '');
                                                        }}
                                                        items={SEAT_HEIGHT_SELECT.map(item => ({
                                                            label: t(item.name),
                                                            value: String(item.value),
                                                            code: String(item.value)
                                                        }))}
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                )}

                                {(formWatch.bikeType || formWatch.engineSizeId || formWatch.bikeTransmission || formWatch.seatHeight || formWatch.isRecommendation || formWatch.dateTime) &&
                                    (
                                        <Button
                                            onClick={() => {
                                                form.reset({
                                                    isRecommendation: undefined,
                                                    engineSizeId: '',
                                                    bikeType: '',
                                                    bikeTransmission: '',
                                                    seatHeight: '',
                                                    dateTime: null
                                                });
                                                setIsClearRental(true);
                                            }}
                                            type='button'
                                            variant='ghost'
                                            className='h-[30px] px-5 py-[5px ] bg-background-gray !rounded-[100px] border border-border-gray flex justify-center items-center gap-[5px] leading-[1]'
                                        >
                                            <X strokeWidth={1} className='size-4' />
                                            {t('button.clear_filter')}
                                        </Button>
                                    )}

                            </form>
                        </Form>
                    </div>

                    {/* List bike */}
                    <div className='px-[10px] grid gap-x-[40px] gap-y-[20px] items-stretch grid-cols-1 point-600:grid-cols-2 point-900:grid-cols-3'>
                        {
                            listBikeLocation.length > 0 && listBikeLocation?.map(item => (
                                <BikeItem key={item?.id} bike={item} locationId={locationDetail?.id} />
                            ))
                        }
                        {
                            listBikeLocation.length === 0 && (
                                <div className='text-center w-full col-span-4 mt-6'>{t('sentences.no_data')}</div>
                            )
                        }
                    </div>

                    {/* Pagination */}
                    <div className='px-[10px] flex justify-center'>
                        {
                            listBikeLocation && listBikeLocation.length > 0 && (
                                <Paginate
                                    isPagenateEU
                                    forcePage={bikePaging.pagination.pageIndex}
                                    pageCount={totalBikePage}
                                    onPageChange={({ selected }) => {
                                        bikePaging.pagination.pageIndex = selected;

                                        let payload: any = {};
                                        const tmpKeepSearchParams: any = keepSearchParams ? { ...keepSearchParams } : undefined;

                                        if (tmpKeepSearchParams) {
                                            const { dateTime, ...newData } = tmpKeepSearchParams;

                                            payload = {
                                                ...newData,
                                                rentalFrom: tmpKeepSearchParams?.dateTime?.from ?
                                                    `${formatDateTime(tmpKeepSearchParams.dateTime.from, DISPPLAY_MOMENT_YMD)} ${tmpKeepSearchParams?.dateTime?.rentalTime === '' ? '00:00' : tmpKeepSearchParams.dateTime.rentalTime}` :
                                                    undefined,
                                                rentalTo: tmpKeepSearchParams?.dateTime?.to ?
                                                    `${formatDateTime(tmpKeepSearchParams.dateTime.to, DISPPLAY_MOMENT_YMD)} ${tmpKeepSearchParams?.dateTime?.returnTime === '' ? '23:59' : tmpKeepSearchParams.dateTime.returnTime}` :
                                                    undefined

                                            };
                                        }

                                        getAllBikeInLocation({
                                            id: locationDetail?.id,
                                            type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS,
                                            ...payload
                                        }, bikePaging);

                                        if (listRef.current) {
                                            (listRef.current as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            const offsetTop = (listRef.current as HTMLElement).getBoundingClientRect().top + window.scrollY - 100;
                                            window.scrollTo({ top: offsetTop, behavior: 'smooth' });
                                        }
                                    }}
                                />
                            )
                        }
                    </div>
                </div>
            </div>
            {
                (id && locations?.filter(item => item.id !== id)?.length > 0) && (
                    <div className='bg-background-gray py-[min(120px,15vw)]'>
                        <div className='wrapper-inner flex flex-col gap-[20px]'>
                            <SubTitleWithBox title={t('words_title.location_same_prefecture')} />

                            <div className='px-[10px] md:px-0'>
                                <Carousel>
                                    <CarouselPrevious className='border-none flex absolute top-1/2 left-[15px] xl:-left-[24px] transform -translate-y-1/2 -translate-x-full shadow-md z-10' />
                                    <CarouselContent className='w-full snap-x snap-mandatory ml-0 gap-[20px]'>
                                        {
                                            locations
                                                .filter(item => item.id !== id)
                                                .map(item => (
                                                    <CarouselItem
                                                        key={item?.id}
                                                        className='pl-0 pr-0 basis-[100%] xsm:basis-[70%] point-600:basis-[48%] md:basis-[45%] point-900:basis-[32%]'
                                                    >
                                                        <LocationItem item={item} />
                                                    </CarouselItem>
                                                ))
                                        }
                                    </CarouselContent>
                                    <CarouselNext className='border-none flex absolute top-1/2 right-[15px] xl:-right-[24px] transform -translate-y-1/2 translate-x-full shadow-md z-10' />
                                </Carousel>
                            </div>

                            <div onClick={handleClickViewAll} className='flex font-semibold items-center justify-end space-x-5 cursor-pointer'>
                                <div className='cursor-pointer'>
                                    一覧を見る
                                </div>
                                <div className='w-6 rounded-full h-6 bg-primaryWeb flex justify-center items-center'>
                                    <ChevronRight className='text-white size-4' />
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
});
